import { useState, useCallback, useMemo } from 'react';
import LessonIssueApiUtils from '@utils/api/lessonIssue';
import LessonApiUtils from '@utils/api/lesson';
import { useFlashMessage } from '@root/hooks/use-flash-message';
import type {
  TutorReportAndResolveIssueModalContainerProps,
  IssueOption,
} from './tutor-report-and-resolve-issue-modal.types';

const FIFTY_MINUTES = 3000000;

export const useTutorReportAndResolveIssueModalService = (
  props: TutorReportAndResolveIssueModalContainerProps,
) => {
  const { hideModal, addModalMessage, clearModalMessage, lesson } = props;

  const { addFlash } = useFlashMessage();

  const [selectedIssueType, setSelectedIssueType] = useState<string | null>(
    null,
  );
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const issueTypeOptions: IssueOption[] = useMemo(
    () => [
      { value: 'Student was absent', id: '0' },
      { value: 'Teacher was absent', id: '1' },
      { value: 'Student-related technical problem', id: '2' },
      { value: 'Tutor-related technical problem', id: '3' },
      { value: 'Other student-related issue', id: '4' },
      { value: 'Other tutor-related issue', id: '5' },
      { value: 'LingualBox related issue', id: '7' },
    ],
    [],
  );

  const actionOptions: IssueOption[] = useMemo(() => {
    const lessonDuration = lesson.end_at - lesson.start_at;
    if (lessonDuration === FIFTY_MINUTES) {
      return [
        { value: 'Mark lesson as completed and issue 100% refund', id: '0' },
        { value: 'Mark lesson as completed and issue 50% rerund', id: '1' },
        { value: 'Mark lesson as completed', id: '2' },
      ];
    }
    return [
      { value: 'Mark lesson as completed and issue 100% refund', id: '0' },
      { value: 'Mark lesson as completed', id: '2' },
    ];
  }, [lesson.end_at, lesson.start_at]);

  const reasonOptions: IssueOption[] = useMemo(
    () => [
      { value: 'Unstable internet connection', id: '0' },
      { value: 'Blackout', id: '1' },
      { value: 'Computer problem', id: '2' },
      { value: 'Sickness', id: '3' },
      { value: 'Taking care of sick family member', id: '4' },
      { value: 'Other tutor-side reason', id: '5' },
      { value: 'LingualBox related reason', id: '6' },
    ],
    [],
  );

  const isStudentAbsentWithMarkComplete =
    selectedIssueType === 'Student was absent' &&
    selectedAction === 'Mark lesson as completed';

  const handleIssueTypeChange = useCallback((value: string) => {
    setSelectedIssueType(value);
  }, []);

  const handleActionChange = useCallback((value: string) => {
    setSelectedAction(value);
  }, []);

  const handleReasonChange = useCallback((value: string) => {
    setSelectedReason(value);
  }, []);

  const handleCommentChange = useCallback((value: string) => {
    setComment(value);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent | React.MouseEvent) => {
      e.preventDefault();

      // Special case: Student was absent + Mark as completed
      if (isStudentAbsentWithMarkComplete) {
        setIsSubmitting(true);
        try {
          await LessonApiUtils.markAsSkippedByTutor(lesson.id);
          addFlash('Successfully marked lesson as skipped', {
            autoDisappear: true,
            sticky: true,
            type: 'success',
          });
          hideModal();
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'An error occurred';
          addModalMessage(errorMessage, {
            autoDisappear: false,
            type: 'error',
          });
          setIsSubmitting(false);
        }
        return;
      }

      // Regular case: needs all fields
      if (
        selectedIssueType === null ||
        selectedAction === null ||
        selectedReason === null ||
        comment === ''
      ) {
        addModalMessage(
          'IssueType, Action, Reason, and Comment cannot be empty.',
          {
            autoDisappear: false,
            type: 'error',
          },
        );
        return;
      }

      clearModalMessage();
      setIsSubmitting(true);

      try {
        await LessonIssueApiUtils.reportAndResolve(
          lesson.id,
          selectedIssueType,
          selectedAction,
          selectedReason,
          comment,
        );

        addFlash('Successfully reported and resolved the issue', {
          autoDisappear: true,
          sticky: true,
          type: 'success',
        });
        hideModal();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'An error occurred';
        addModalMessage(errorMessage, {
          autoDisappear: false,
          type: 'error',
        });
        setIsSubmitting(false);
      }
    },
    [
      isStudentAbsentWithMarkComplete,
      selectedIssueType,
      selectedAction,
      selectedReason,
      comment,
      lesson.id,
      addModalMessage,
      clearModalMessage,
      addFlash,
      hideModal,
    ],
  );

  const handleCancel = useCallback(() => {
    hideModal();
  }, [hideModal]);

  return {
    selectedIssueType,
    selectedAction,
    selectedReason,
    comment,
    issueTypeOptions,
    actionOptions,
    reasonOptions,
    isSubmitting,
    showReasonAndComment: !isStudentAbsentWithMarkComplete,
    showPendingInfo: isStudentAbsentWithMarkComplete,
    submitButtonText: isStudentAbsentWithMarkComplete ? 'Save' : 'Resolve',
    message: props.message,
    messageType: props.messageType,
    onIssueTypeChange: handleIssueTypeChange,
    onActionChange: handleActionChange,
    onReasonChange: handleReasonChange,
    onCommentChange: handleCommentChange,
    onSubmit: handleSubmit,
    onCancel: handleCancel,
  };
};
